import { useEffect, useRef } from 'react';
import {
  qAppConfig,
  qInventorySupplierById,
  qPurchaseOrderForReceive,
  qPurchaseOrderLinesByOrder,
  qPurchaseOrderPrintSettings,
  type AppConfigRow,
  type InventorySupplierRow,
  type PurchaseOrderLineRow,
  type PurchaseOrderPrintSettingsRow,
  type PurchaseOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { triggerPrint } from '../clinic/prescription-print/print-utils';
import {
  PURCHASE_ORDER_PRINT_STYLES,
  resolveBuyer,
  resolvePurchaseOrderSettings,
  resolveSupplier,
  toLayoutSettings,
} from '../clinic/purchase-order-print/print-utils';
import {
  PurchaseOrderPrintLayout,
  type PurchaseOrderPrintLineItem,
  type PurchaseOrderPrintMeta,
  type PurchaseOrderPrintTotals,
} from '../clinic/purchase-order-print/PurchaseOrderPrintLayout';

/**
 * PRD.239 — Purchase Order print mount.
 *
 * Reads the PO + lines + supplier + app_config + saved
 * `purchase_order_print_settings`, renders the layout into a hidden
 * `.po-print-only` div, and fires `triggerPrint('purchase-order')`
 * once everything is loaded. Pure read path — no DB mutations, no
 * stock movements. Mount it as a sibling of the page content; when
 * `request` goes non-null it fetches and prints, then calls
 * `onAfterPrint` so the caller can reset state.
 *
 * Snapshot-first display per PRD.239 §12: supplier_name + item/sku/unit
 * snapshots from the saved PO/PO lines are the source of truth. Live
 * supplier_master is joined only for secondary contact details. Saved
 * print settings win over defaults; defaults only apply when the wizard
 * has never been saved for this org.
 */

export interface PurchaseOrderPrintRequest {
  purchaseOrderId: string;
}

export function PurchaseOrderPrintMount({
  request,
  onAfterPrint,
}: {
  request: PurchaseOrderPrintRequest | null;
  onAfterPrint: () => void;
}) {
  const fmt = useFormatMoney();
  const purchaseOrderId = request?.purchaseOrderId ?? '';

  const { data: poRows = [] } = usePowerSyncQuery<PurchaseOrderRow>(
    qPurchaseOrderForReceive(purchaseOrderId),
  );
  const po = purchaseOrderId ? (poRows[0] ?? null) : null;

  const { data: lineRows = [] } = usePowerSyncQuery<PurchaseOrderLineRow>(
    qPurchaseOrderLinesByOrder(purchaseOrderId),
  );

  // Supplier master is optional — pre-PRD.207 POs may have free-text
  // suppliers (supplier_id IS NULL). When unset, fall back to the
  // snapshot on the PO and skip the master fetch.
  const supplierId = po?.supplier_id ?? '';
  const { data: supplierRows = [] } = usePowerSyncQuery<InventorySupplierRow>(
    qInventorySupplierById(supplierId),
  );
  const supplierMaster = supplierId ? (supplierRows[0] ?? null) : null;

  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const appConfig = appConfigRows[0] ?? null;

  // PRD.239 — Saved settings row (clinic_id IS NULL org-default).
  // The query returns at most one row; we fall back to defaults only
  // when no row exists yet.
  const { data: settingsRows = [] } = usePowerSyncQuery<PurchaseOrderPrintSettingsRow>(
    qPurchaseOrderPrintSettings(),
  );
  const savedSettings = settingsRows[0] ?? null;

  // Fire the print once data is loaded. The guard ref prevents
  // re-firing while React re-renders (PowerSync can update the rows
  // array referentially even when contents are stable).
  const firedForRef = useRef<string | null>(null);
  const dataReady =
    !!request &&
    !!po &&
    Array.isArray(lineRows) &&
    !!appConfig &&
    // If a supplier_id is set, wait for the supplier master row too.
    (!supplierId || supplierMaster != null);

  useEffect(() => {
    if (!request) {
      firedForRef.current = null;
      return;
    }
    if (!dataReady) return;
    if (firedForRef.current === request.purchaseOrderId) return;
    firedForRef.current = request.purchaseOrderId;

    const handleAfter = () => {
      window.removeEventListener('afterprint', handleAfter);
      onAfterPrint();
    };
    window.addEventListener('afterprint', handleAfter);
    // Fallback in case `afterprint` never fires (Safari / embedded webviews).
    const fallback = setTimeout(handleAfter, 2500);
    triggerPrint('purchase-order');
    return () => {
      clearTimeout(fallback);
      window.removeEventListener('afterprint', handleAfter);
    };
  }, [request, dataReady, onAfterPrint]);

  if (!request || !po) {
    // Still inject the stylesheet so the scoped @page rule is present
    // even on first render — avoids a one-frame flash if the user spams
    // the print button.
    return <style>{PURCHASE_ORDER_PRINT_STYLES}</style>;
  }

  const settingsUI = resolvePurchaseOrderSettings(savedSettings);
  const layoutSettings = toLayoutSettings(settingsUI);
  const buyer = resolveBuyer(settingsUI, appConfig);
  const supplier = resolveSupplier(po.supplier_name, supplierMaster);

  const meta: PurchaseOrderPrintMeta = {
    poNumber: po.po_number ?? po.id.slice(0, 8),
    poDateIso: po.po_date ?? po.created_at ?? '',
    status: po.status ?? undefined,
    amountsAre: po.amounts_are ?? 'tax_exclusive',
    preparedBy: po.created_by ?? '',
  };

  const items: PurchaseOrderPrintLineItem[] = lineRows.map((l) => ({
    itemName: l.item_name_snapshot ?? '—',
    sku: l.sku_snapshot ?? '',
    unit: l.unit_snapshot ?? '',
    quantityOrdered: l.quantity_ordered ?? 0,
    bonusQuantity: l.bonus_quantity ?? 0,
    rate: l.rate ?? 0,
    amount: l.amount ?? 0,
    remarks: l.remarks ?? '',
  }));

  const subtotal = items.reduce((s, l) => s + (l.amount || 0), 0);
  const totals: PurchaseOrderPrintTotals = {
    subtotal,
    totalAmount: po.total_amount ?? subtotal,
  };

  return (
    <>
      <style>{PURCHASE_ORDER_PRINT_STYLES}</style>
      <div className="po-print-only" aria-hidden>
        <PurchaseOrderPrintLayout
          settings={layoutSettings}
          buyer={buyer}
          supplier={supplier}
          meta={meta}
          items={items}
          totals={totals}
          notes={po.notes ?? ''}
          formatMoney={fmt}
        />
      </div>
    </>
  );
}
